import { useEffect, useMemo, useState } from "react";
import type { RestaurantLite } from "../src/types";
import { Brand } from "./components/Brand";
import { ChipGroup } from "./components/ChipGroup";
import { RestaurantCard } from "./components/RestaurantCard";
import { Tabs } from "./components/Tabs";
import { deriveArea } from "./lib/areaDerivation";
import { haversineMi } from "./lib/distance";
import { activeOrigin, LOCATIONS } from "./lib/locations";
import { prioritize, startPrefetch } from "./lib/menuPrefetch";
import { loadSearchIndex, searchMenuItems } from "./lib/search";
import { deriveServiceFromLite } from "./lib/serviceDerivation";
import { useStore } from "./lib/store";
import type { ExplorerRestaurant, SortKey } from "./lib/types-explorer";
import { loadVersion, type Version } from "./lib/version";

const SERVICE_OPTIONS = [
  "Quick Service",
  "Table Service",
  "Snack",
  "Lounge",
  "Fine Dining",
  "Buffet",
  "Family-style",
  "Character Dining",
];
const PRICE_OPTIONS = ["$", "$$", "$$$", "$$$$"];

export function App() {
  const s = useStore();
  const [version, setVersion] = useState<Version | null>(null);

  useEffect(() => {
    loadVersion().then(setVersion);
  }, []);

  // Initial data load — intentional one-time fetch on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time fetch; setRestaurants is a stable zustand action
  useEffect(() => {
    fetch("/data/restaurants.json")
      .then((r) => r.json() as Promise<RestaurantLite[]>)
      .then((lites) => {
        const explorer: ExplorerRestaurant[] = lites.map((l) => ({
          ...l,
          area: deriveArea(l),
          service: deriveServiceFromLite(l),
        }));
        s.setRestaurants(explorer);

        // Schedule background prefetch of every menu so the first card-expand
        // is already populated. requestIdleCallback yields to first paint;
        // setTimeout(0) is the Safari fallback.
        const slugs = lites.map((l) => l.slug);
        const kick = () => startPrefetch(slugs);
        // biome-ignore lint/suspicious/noExplicitAny: feature-detect
        const ric = (window as any).requestIdleCallback as
          | ((cb: () => void, opts?: { timeout: number }) => number)
          | undefined;
        if (typeof ric === "function") ric(kick, { timeout: 2000 });
        else setTimeout(kick, 0);
      })
      .catch((err) => console.error("data load failed:", err));
  }, []);

  // If a previous session left us on `geo`, refresh the fix on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time mount; requestGeo is a stable zustand action
  useEffect(() => {
    if (s.originId === "geo") void s.requestGeo();
  }, []);

  // Lazy-load the search index on first non-empty query
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — trigger only on searchQuery; stable zustand actions never change
  useEffect(() => {
    if (s.searchQuery.trim() && !s.searchIndex && !s.searchIndexLoading) {
      s.setSearchIndexLoading(true);
      loadSearchIndex()
        .then(s.setSearchIndex)
        .catch((e) => {
          console.error("search index load failed:", e);
          s.setSearchIndexLoading(false);
        });
    }
  }, [s.searchQuery]);

  const origin = activeOrigin(s.originId, s.geoCoords);

  // Approved/vetoed counts
  const approvedCount = useMemo(
    () => Object.values(s.statusMap).filter((v) => v === "approved").length,
    [s.statusMap]
  );
  const vetoedCount = useMemo(
    () => Object.values(s.statusMap).filter((v) => v === "vetoed").length,
    [s.statusMap]
  );

  // Area chip options derived from data
  const areaOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of s.restaurants) counts.set(r.area, (counts.get(r.area) ?? 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, label: value, count }));
  }, [s.restaurants]);

  // Filtered + sorted list
  const visible = useMemo(() => {
    let list = s.restaurants.map((r) => ({
      ...r,
      _status: s.statusMap[r.slug] ?? null,
      _distance: haversineMi(origin, r.geo),
    }));

    if (s.tab === "shortlist") list = list.filter((r) => r._status === "approved");
    else list = list.filter((r) => r._status !== "vetoed");

    if (s.parkFilter.length) list = list.filter((r) => s.parkFilter.includes(r.area));
    if (s.serviceFilter.length) list = list.filter((r) => s.serviceFilter.includes(r.service));
    if (s.priceFilter.length) list = list.filter((r) => s.priceFilter.includes(r.priceRange[0]));
    if (s.hideNoGF) list = list.filter((r) => r.safeCount > 0);
    if (!s.showUnknown) list = list.filter((r) => r.glutenCounts.unknown === 0 || r.safeCount > 0);
    if (s.searchQuery.trim()) {
      const q = s.searchQuery.toLowerCase();
      // Always allow restaurant-name/cuisine/area substring as a fast first-pass.
      const nameSubstring = (r: (typeof list)[number]) =>
        r.name.toLowerCase().includes(q) ||
        r.cuisine.join(" ").toLowerCase().includes(q) ||
        r.area.toLowerCase().includes(q);

      if (s.searchIndex) {
        // Menu-item search: union with the substring match so name hits surface even before fuzzy matches kick in.
        const hits = searchMenuItems(s.searchIndex, s.searchQuery, 500);
        const matchedSlugs = new Set(hits.map((h) => h.restaurantSlug));
        list = list.filter((r) => matchedSlugs.has(r.slug) || nameSubstring(r));
      } else {
        // Index hasn't loaded yet — fall back to substring only.
        list = list.filter(nameSubstring);
      }
    }

    const priceVal = (p: string) => p.length;
    list.sort((a, b) => {
      switch (s.sortBy) {
        case "safe":
          return b.safeCount - a.safeCount || a.name.localeCompare(b.name);
        case "distance":
          return a._distance - b._distance;
        case "price":
          return (
            priceVal(a.priceRange[0]) - priceVal(b.priceRange[0]) || a.name.localeCompare(b.name)
          );
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    return list;
  }, [
    s.restaurants,
    s.statusMap,
    s.parkFilter,
    s.serviceFilter,
    s.priceFilter,
    s.hideNoGF,
    s.showUnknown,
    s.searchQuery,
    s.sortBy,
    s.tab,
    origin,
    s.searchIndex,
  ]);

  function loadMenu(slug: string) {
    // Side-effect call — prioritize writes the menu into the store on resolve
    // and reuses any in-flight or queued fetch.
    prioritize(slug).catch((err) => console.error("menu load failed:", slug, err));
  }

  const exploreCount = s.restaurants.length - vetoedCount;

  return (
    <div className={`max-w-[1280px] mx-auto px-7 pt-6 pb-20 max-md:px-4 layout-${s.layout}`}>
      <header className="flex items-center justify-between gap-4 pb-4 border-b border-(--color-line) mb-4">
        <Brand />
        <div className="flex items-center gap-2">
          <Tabs
            tabs={[
              { value: "explore", label: "Explore", count: exploreCount },
              { value: "shortlist", label: "Shortlist", count: approvedCount },
            ]}
            active={s.tab}
            onChange={s.setTab}
          />
          <div className="flex bg-(--color-paper-2) p-[3px] rounded-lg border border-(--color-line)">
            {(["compact", "cards"] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => s.setLayout(l)}
                className={`px-2.5 py-1.5 rounded-md text-[12px] ${
                  s.layout === l
                    ? "bg-(--color-paper) text-(--color-ink) shadow-(--shadow-sm)"
                    : "text-(--color-ink-2) hover:text-(--color-ink)"
                }`}
                aria-label={`${l} layout`}
              >
                {l === "compact" ? "≡" : "▦"}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section className="flex flex-col gap-3 mb-4">
        <div className="flex flex-wrap gap-2.5 items-start">
          <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-(--color-paper) border border-(--color-line) rounded-lg px-3 h-[38px] text-(--color-ink-3) focus-within:border-(--color-accent) focus-within:shadow-[0_0_0_3px_var(--color-accent-soft)]">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M9 9l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search restaurants, cuisines, areas…"
              className="flex-1 border-0 outline-none bg-transparent text-[13px] text-(--color-ink)"
              value={s.searchQuery}
              onChange={(e) => s.setSearchQuery(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 bg-(--color-paper) border border-(--color-line) rounded-lg pl-3 pr-1 h-[38px]">
            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-(--color-ink-3)">
              Near
            </span>
            <select
              className="border-0 outline-none bg-transparent text-[13px] text-(--color-ink) px-2 h-[36px] cursor-pointer max-w-[220px]"
              value={s.originId}
              onChange={(e) => s.setOriginId(e.target.value)}
            >
              <option value="geo" disabled={s.geoStatus === "acquiring"}>
                {s.geoStatus === "ready"
                  ? "📍 My location"
                  : s.geoStatus === "acquiring"
                    ? "📍 Locating…"
                    : "📍 Use my location"}
              </option>
              {(["Parks", "Areas", "Resorts"] as const).map((g) => (
                <optgroup key={g} label={g}>
                  {LOCATIONS.filter((l) => l.group === g).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          {s.originId === "geo" && s.geoStatus === "ready" && (
            <button
              type="button"
              onClick={() => void s.requestGeo(0)}
              aria-label="Refresh my location"
              className="flex items-center justify-center w-[38px] h-[38px] rounded-lg border border-(--color-line) bg-(--color-paper) text-(--color-ink-2) hover:text-(--color-ink)"
            >
              <span aria-hidden="true">↻</span>
            </button>
          )}
          {s.originId === "geo" && s.geoStatus === "acquiring" && (
            <div
              aria-live="polite"
              className="flex items-center justify-center w-[38px] h-[38px] rounded-lg border border-(--color-line) bg-(--color-paper) text-(--color-ink-3)"
            >
              <span aria-hidden="true" className="animate-spin">
                ↻
              </span>
            </div>
          )}
          <label className="flex items-center gap-2 bg-(--color-paper) border border-(--color-line) rounded-lg pl-3 pr-1 h-[38px]">
            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-(--color-ink-3)">
              Sort
            </span>
            <select
              className="border-0 outline-none bg-transparent text-[13px] text-(--color-ink) px-2 h-[36px] cursor-pointer"
              value={s.sortBy}
              onChange={(e) => s.setSortBy(e.target.value as SortKey)}
            >
              <option value="safe">Most safe items</option>
              <option value="distance">Distance</option>
              <option value="price">Price (low → high)</option>
              <option value="name">Name (A–Z)</option>
            </select>
          </label>
        </div>

        <div className="flex flex-wrap gap-2.5">
          <ChipGroup
            label="Area"
            options={areaOptions}
            value={s.parkFilter}
            onToggle={s.togglePark}
          />
        </div>

        <div className="flex flex-wrap gap-6 items-start">
          <ChipGroup
            label="Service"
            options={SERVICE_OPTIONS.map((v) => ({ value: v, label: v }))}
            value={s.serviceFilter}
            onToggle={s.toggleService}
          />
          <ChipGroup
            label="Price"
            options={PRICE_OPTIONS.map((v) => ({ value: v, label: v }))}
            value={s.priceFilter}
            onToggle={s.togglePrice}
          />
          <div className="flex flex-col gap-1.5">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-(--color-ink-3)">
              Show
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => s.setHideNoGF(!s.hideNoGF)}
                className={`chip inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-medium text-[12px] border ${
                  s.hideNoGF
                    ? "bg-(--color-accent) border-(--color-accent) text-(--color-paper)"
                    : "bg-(--color-paper) border-(--color-line) text-(--color-ink-2)"
                }`}
              >
                <span className="w-2.5">{s.hideNoGF ? "✓" : ""}</span> Hide no-GF spots
              </button>
              <button
                type="button"
                onClick={() => s.setShowUnknown(!s.showUnknown)}
                className={`chip inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-medium text-[12px] border ${
                  s.showUnknown
                    ? "bg-(--color-accent) border-(--color-accent) text-(--color-paper)"
                    : "bg-(--color-paper) border-(--color-line) text-(--color-ink-2)"
                }`}
              >
                <span className="w-2.5">{s.showUnknown ? "✓" : ""}</span> Include "ask" items
              </button>
            </div>
          </div>
        </div>

        {(s.geoStatus === "denied" || s.geoStatus === "unavailable" || s.geoStatus === "error") && (
          <div
            role="status"
            className={`flex items-center gap-2 text-[12px] rounded-lg px-3 py-2 ${
              s.geoStatus === "error"
                ? "text-(--color-bad) bg-(--color-bad-soft) border border-(--color-bad)"
                : "text-(--color-warn-ink) bg-(--color-warn-soft) border border-(--color-warn)"
            }`}
          >
            <span className="flex-1">
              {s.geoStatus === "denied" &&
                "Location permission denied. Pick a starting point instead."}
              {s.geoStatus === "unavailable" && "Geolocation isn't supported by this browser."}
              {s.geoStatus === "error" && (
                <>
                  Couldn't get your location: {s.geoError ?? "unknown error"}.{" "}
                  <button
                    type="button"
                    onClick={() => void s.requestGeo()}
                    className="underline font-medium"
                  >
                    Retry
                  </button>
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => s.clearGeoError()}
              aria-label="Dismiss"
              className={`hover:opacity-70 ${
                s.geoStatus === "error" ? "text-(--color-bad)" : "text-(--color-warn-ink)"
              }`}
            >
              ✕
            </button>
          </div>
        )}
        <div className="flex items-baseline gap-1.5 pt-1.5 border-t border-dashed border-(--color-line)">
          <span className="font-mono font-semibold text-[16px] text-(--color-ink)">
            {visible.length}
          </span>
          <span className="text-(--color-ink-3) text-[13px]">
            {s.tab === "shortlist" ? "on your shortlist" : "restaurants"}
          </span>
          {s.tab === "explore" && vetoedCount > 0 && (
            <button
              type="button"
              onClick={() => s.clearAllVetoed()}
              className="ml-auto bg-transparent border-0 text-(--color-accent) font-medium text-[12px] px-2 py-1 rounded hover:bg-(--color-accent-soft)"
            >
              Restore {vetoedCount} vetoed
            </button>
          )}
        </div>
      </section>

      <main
        className={`grid gap-3 ${
          s.layout === "compact"
            ? "grid-cols-1 gap-0 border border-(--color-line) rounded-(--radius-card) overflow-hidden bg-(--color-paper) grid-compact"
            : "grid-cols-[repeat(auto-fill,minmax(340px,1fr))] grid-cards"
        }`}
      >
        {visible.length === 0 && (
          <div className="col-span-full py-16 px-5 text-center bg-(--color-paper) border border-dashed border-(--color-line) rounded-(--radius-card) text-(--color-ink-3)">
            {s.tab === "shortlist" ? (
              <>
                <h3 className="font-serif text-(--color-ink) m-0 mb-1.5">No approved spots yet.</h3>
                <p className="m-0 text-[13px]">
                  Tap <strong>Approve</strong> on a restaurant in Explore to add it here.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-serif text-(--color-ink) m-0 mb-1.5">No restaurants match.</h3>
                <p className="m-0 text-[13px]">
                  Try clearing filters or unchecking "Hide no-GF spots".
                </p>
              </>
            )}
          </div>
        )}
        {visible.map((r) => (
          <RestaurantCard
            key={r.slug}
            r={r}
            status={r._status}
            distance={r._distance}
            expanded={s.expandedSlugs.includes(r.slug)}
            layout={s.layout}
            loadedMenu={s.loadedMenus[r.slug]}
            onToggle={() => s.toggleExpanded(r.slug)}
            onSet={(next) => s.setStatus(r.slug, next)}
            onLoadMenu={() => loadMenu(r.slug)}
          />
        ))}
      </main>

      <footer className="mt-8 pt-4 text-center text-[11px] text-(--color-ink-3) font-mono">
        <span title={version?.date ? `built ${version.date}` : undefined}>
          build {version?.sha ?? "…"}
        </span>
        {s.prefetchTotal > 0 && s.prefetchSettled < s.prefetchTotal && (
          <>
            <span className="text-(--color-line)"> · </span>
            <span>menus {s.prefetchSettled}/{s.prefetchTotal}</span>
          </>
        )}
      </footer>
    </div>
  );
}
